import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from './icons.jsx';
import { t } from '../i18n/index.jsx';

const ToastCtx = createContext(null);
const ConfirmCtx = createContext(null);

/**
 * App-wide feedback layer: non-blocking toasts (bottom-right, auto-dismiss)
 * and a styled, promise-based confirm dialog. Replaces native alert()/confirm()
 * so user feedback stays inside the product's visual contract.
 *
 *   const toast = useToast();   toast('Project saved');  toast(err.message, { tone: 'error' });
 *   const confirm = useConfirm(); if (!(await confirm('Delete this?'))) return;
 */
export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message, opts = {}) => {
    const id = ++idRef.current;
    const tone = opts.tone || 'success';
    const duration = opts.duration ?? 3000;
    setToasts((list) => [...list, { id, message, tone }]);
    if (duration > 0) setTimeout(() => dismiss(id), duration);
    return id;
  }, [dismiss]);

  const [dialog, setDialog] = useState(null);
  const confirm = useCallback((opts) => {
    const o = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => setDialog({ ...o, resolve }));
  }, []);

  const closeDialog = useCallback((result) => {
    setDialog((d) => { d?.resolve(result); return null; });
  }, []);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeDialog(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, closeDialog]);

  return (
    <ToastCtx.Provider value={toast}>
      <ConfirmCtx.Provider value={confirm}>
        {children}

        <div className="toast-stack" role="status" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`toast ${t.tone}`} onClick={() => dismiss(t.id)}>
              <span className="toast-dot" />
              <span className="toast-msg">{t.message}</span>
            </div>
          ))}
        </div>

        {dialog && createPortal(
          <div className="modal-backdrop" onMouseDown={() => closeDialog(false)}>
            <div className="modal confirm-modal" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
              <div className="modal-head">
                <h3>{dialog.title || t('Please confirm')}</h3>
                <button className="icon-btn" onClick={() => closeDialog(false)} aria-label="Close">
                  <IconClose width={18} height={18} />
                </button>
              </div>
              <div className="modal-body">{dialog.message}</div>
              <div className="modal-foot">
                <div className="modal-actions">
                  <div className="spacer" />
                  <button className="btn ghost" onClick={() => closeDialog(false)}>
                    {dialog.cancelLabel || t('Cancel')}
                  </button>
                  <button
                    className={`btn ${dialog.danger ? 'danger-solid' : 'primary'}`}
                    autoFocus
                    onClick={() => closeDialog(true)}
                  >
                    {dialog.confirmLabel || t('Confirm')}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </ConfirmCtx.Provider>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within <FeedbackProvider>');
  return ctx;
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm must be used within <FeedbackProvider>');
  return ctx;
}
